import { useState, useEffect, useRef, RefObject } from 'react';

interface UseClickOutsideProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    buttonRef: RefObject<HTMLElement | null>;
}

export const useClickOutside = ({ isOpen, setIsOpen, buttonRef }: UseClickOutsideProps) => {
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, setIsOpen, buttonRef]);

    return popupRef;
};

export const useDropdownPositioning = (buttonRef: RefObject<HTMLElement | null>, isOpen: boolean) => {
    const [position, setPosition] = useState({ left: 0, top: 0 });

    useEffect(() => {
        const updatePosition = () => {
            const button = buttonRef.current;
            if (!button) return;

            const rect = button.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            setPosition({
                left: rect.left,
                top: Math.min(rect.top, windowHeight - 50)
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [buttonRef, isOpen]);

    return position;
};